import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * فئتا أدوات الموصل: المعلَّم يُنفَّذ بلا موافقة ويظهر في صندوق الاعتماد
 * «نُفّذ آلياً»؛ والمقفل (إرسال لخارج الفريق، نشر، مال، اعتماد، حذف) يبقى
 * بموافقة ولا يُعلَّم. وسقف يومي لحصّة الصوت عبر التنفيذ الآلي.
 */
function ConnectorAutoApprove() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [tools, setTools] = useState([]);
  const [hours, setHours] = useState('');
  const [hosts, setHosts] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const apply = (d) => {
    setConfig(d);
    setTools(d.tools || []);
    setHours(String(Math.round((d.daily_audio_seconds || 0) / 360) / 10));
    setHosts((d.auto_url_hosts || []).join('\n'));
  };
  useEffect(() => { api('/settings/connector-auto-approve').then(apply).catch(() => {}); }, [api]);

  if (!config) return null;

  const toggle = (name) => setTools(t => (t.includes(name) ? t.filter(n => n !== name) : [...t, name]));
  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      apply(await api('/settings/connector-auto-approve', {
        method: 'PUT',
        body: JSON.stringify({
          tools,
          daily_audio_seconds: Math.round(parseFloat(hours || '0') * 3600),
          auto_url_hosts: hosts.split(/[\s,،]+/).map(h => h.trim().toLowerCase()).filter(Boolean),
        }),
      }));
      setNote({ type: 'success', text: 'حُفظت فئتا الموصل' });
    } catch (err) {
      setNote({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '12px 14px', borderRadius: 8, marginBottom: 20, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>التنفيذ الآلي لأدوات الموصل</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 10px', lineHeight: 1.8 }}>
        المعلَّم يُنفَّذ بلا موافقة ويُسجَّل ويظهر في صندوق الاعتماد «نُفّذ آلياً». المقفل يبقى بموافقة دائماً.
        استُهلك اليوم عبر التنفيذ الآلي: {Math.round((config.used_today_seconds || 0) / 60)} دقيقة صوت.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, marginBottom: 10 }}>
        {(config.tools_catalog || []).map(t => (
          <label key={t.name} style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', opacity: t.locked ? 0.55 : 1 }} title={t.locked || ''}>
            <input type="checkbox" disabled={!!t.locked} checked={!t.locked && tools.includes(t.name)} onChange={() => toggle(t.name)} />
            {t.title}{t.spends_audio ? ' 🎙️' : ''}{t.locked ? ` — ${t.locked}` : ''}
          </label>
        ))}
      </div>
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        نطاقات يُجلب منها التفريغ من رابط آلياً (نطاق في كل سطر، والفرعيّ منه مشمول). غيرها يحتاج موافقة، وغيرها بنصّ استعلام (?…) يُرفض:
      </div>
      <textarea
        className="form-input"
        rows={4}
        dir="ltr"
        value={hosts}
        onChange={(e) => setHosts(e.target.value)}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12 }}>حدّ الصوت اليومي عبر التنفيذ الآلي (ساعات) 🎙️:</span>
        <input type="number" min="0" step="0.5" className="form-input" value={hours} onChange={(e) => setHours(e.target.value)} style={{ maxWidth: 100 }} />
        <button className="btn btn-sm btn-outline" disabled={busy} onClick={save}>حفظ</button>
        {note && <span style={{ fontSize: 12, color: note.type === 'error' ? 'var(--c-coral)' : 'var(--c-green, inherit)' }}>{note.text}</span>}
      </div>
    </div>
  );
}

export default function Settings() {
  const { api, apiUpload } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [glossary, setGlossary] = useState([]);
  const [glossarySuggested, setGlossarySuggested] = useState(0);
  const [glossarySaving, setGlossarySaving] = useState(false);
  const [glossaryScope, setGlossaryScope] = useState('');
  const [sheikhs, setSheikhs] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [logEntries, setLogEntries] = useState(null);
  const [logFilter, setLogFilter] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [ytApi, setYtApi] = useState(null);
  const [ytApiInput, setYtApiInput] = useState('');
  const [ytApiSaving, setYtApiSaving] = useState(false);
  const [autoBenefitsSaving, setAutoBenefitsSaving] = useState(false);
  const [quotaDraft, setQuotaDraft] = useState('');
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishForm, setPublishForm] = useState({ mode: '', endpoint: '', token: '', default_status: 'draft' });
  const [publishSaving, setPublishSaving] = useState(false);
  const [cobaltKey, setCobaltKey] = useState(null);
  const [cobaltKeyInput, setCobaltKeyInput] = useState('');
  const [cobaltKeySaving, setCobaltKeySaving] = useState(false);
  const [proxy, setProxy] = useState(null);
  const [proxyInput, setProxyInput] = useState('');
  const [proxySaving, setProxySaving] = useState(false);
  const [probeUrl, setProbeUrl] = useState('');
  const [probeResult, setProbeResult] = useState(null);
  const [probing, setProbing] = useState(false);
  const [pullResult, setPullResult] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [ytCookies, setYtCookies] = useState(null);
  const [cookiesSaving, setCookiesSaving] = useState(false);
  const [groqKeys, setGroqKeys] = useState([]); // array of masked existing keys
  const [newGroqKey, setNewGroqKey] = useState('');
  const [groqKeysSaving, setGroqKeysSaving] = useState(false);
  const [paidSaving, setPaidSaving] = useState(false);
  const [desk, setDesk] = useState(null);
  const [deskSaving, setDeskSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api('/settings/youtube-api')
      .then(d => { setYtApi(d); setYtApiInput(d.url || ''); })
      .catch(() => {});
    api('/settings/http-proxy')
      .then(setProxy)
      .catch(() => {});
    api('/settings/youtube-cookies')
      .then(setYtCookies)
      .catch(() => {});
    api('/settings/publish-target')
      .then(t => {
        setPublishTarget(t);
        setPublishForm({ mode: t.mode || '', endpoint: t.endpoint || '', token: '', default_status: t.default_status });
      })
      .catch(() => {});
    api('/settings/cobalt-key')
      .then(setCobaltKey)
      .catch(() => {});
    api('/settings/support')
      .then(d => setDesk({ ...d, canned: d.canned || [] }))
      .catch(() => {});
    api('/settings/transcription')
      .then(setStatus)
      .catch(() => setMessage({ type: 'error', text: 'تعذر تحميل الإعدادات' }))
      .finally(() => setLoading(false));
    api('/settings/transcription/keys')
      .then(d => setGroqKeys(d.keys || []))
      .catch(() => {});
    loadGlossary();
    api('/clients')
      .then(list => setSheikhs((list || []).filter(c => c.type === 'sheikh')))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await api('/settings/transcription', {
        method: 'PUT',
        body: JSON.stringify({ api_key: apiKey.trim() }),
      });
      setStatus(res);
      setApiKey('');
      setMessage({ type: 'success', text: 'تم حفظ المفتاح بنجاح' });
      // Refresh the multi-key list since saving the legacy key also re-syncs it
      api('/settings/transcription/keys').then(d => setGroqKeys(d.keys || [])).catch(() => {});
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setSaving(false);
    }
  };

  const savePaidTier = async (paid) => {
    setPaidSaving(true);
    setMessage(null);
    try {
      setStatus(await api('/settings/transcription', {
        method: 'PUT',
        body: JSON.stringify({ paid_tier: paid }),
      }));
      setMessage({ type: 'success', text: paid ? 'تم تفعيل حدود الاشتراك المدفوع' : 'تم الرجوع إلى حدود الاشتراك المجاني' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setPaidSaving(false);
    }
  };

  const saveAutoBenefits = async (auto) => {
    // حالة انشغال خاصّة به: مشاركتها مع ضابط الشريحة كانت تُعطّل ضابطاً آخر
    setAutoBenefitsSaving(true);
    setMessage(null);
    try {
      setStatus(await api('/settings/transcription', {
        method: 'PUT',
        body: JSON.stringify({ auto_benefits: auto }),
      }));
      setMessage({
        type: 'success',
        text: auto ? 'سيُستخرج الفوائد تلقائياً بعد كل تفريغ' : 'الاستخراج التلقائي معطّل — الزرّ يعمل دائماً',
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setAutoBenefitsSaving(false);
    }
  };

  const saveHourlyQuota = async () => {
    const seconds = parseInt(quotaDraft, 10);
    if (!seconds || seconds < 600) {
      setMessage({ type: 'error', text: 'الحصّة بالثواني، ولا تقلّ عن 600' });
      return;
    }
    setQuotaSaving(true);
    setMessage(null);
    try {
      setStatus(await api('/settings/transcription', {
        method: 'PUT',
        body: JSON.stringify({ hourly_audio_seconds: seconds }),
      }));
      setQuotaDraft('');
      setMessage({ type: 'success', text: `حصّة كل مفتاح: ${seconds} ثانية صوت في الساعة` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setQuotaSaving(false);
    }
  };

  const addGroqKey = async () => {
    if (!newGroqKey.trim()) return;
    setGroqKeysSaving(true);
    setMessage(null);
    try {
      const res = await api('/settings/transcription/keys', {
        method: 'POST',
        body: JSON.stringify({ api_key: newGroqKey.trim() }),
      });
      setGroqKeys(res.keys || []);
      setNewGroqKey('');
      setMessage({ type: 'success', text: `تمت الإضافة. عدد المفاتيح: ${res.count}` });
      api('/settings/transcription').then(setStatus).catch(() => {});
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الإضافة' });
    } finally {
      setGroqKeysSaving(false);
    }
  };

  const removeGroqKey = async (index) => {
    if (!window.confirm('حذف هذا المفتاح؟')) return;
    setGroqKeysSaving(true);
    try {
      const res = await api(`/settings/transcription/keys/${index}`, { method: 'DELETE' });
      setGroqKeys(res.keys || []);
      setMessage({ type: 'success', text: `تم الحذف. عدد المفاتيح المتبقية: ${res.count}` });
      api('/settings/transcription').then(setStatus).catch(() => {});
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحذف' });
    } finally {
      setGroqKeysSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const res = await api('/settings/transcription/test', { method: 'POST' });
      setMessage({
        type: res.ok ? 'success' : 'error',
        text: res.ok ? (res.message || 'الاتصال ناجح') : (res.error || 'فشل الاتصال'),
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل اختبار الاتصال' });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('هل أنت متأكد من حذف المفتاح؟')) return;
    try {
      const res = await api('/settings/transcription', { method: 'DELETE' });
      setStatus(res);
      setMessage({ type: 'success', text: 'تم حذف المفتاح' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحذف' });
    }
  };

  // القاموس صار جدولاً لكل شيخ: الإضافة والحذف تُحفظان فوراً بدل «حفظ الكل»
  const loadGlossary = async (scope = glossaryScope) => {
    try {
      const qs = scope === 'global' ? '?scope=global' : scope ? `?client_id=${scope}` : '';
      const d = await api(`/settings/glossary${qs}`);
      setGlossary(d.items || []);
      setGlossarySuggested(d.suggested || 0);
    } catch { setGlossary([]); }
  };

  const addTerm = async () => {
    if (!newTerm.trim()) return;
    setGlossarySaving(true);
    try {
      await api('/settings/glossary', {
        method: 'POST',
        body: JSON.stringify({
          term: newTerm.trim(),
          note: newNote.trim() || null,
          client_id: glossaryScope && glossaryScope !== 'global' ? Number(glossaryScope) : null,
        }),
      });
      setNewTerm(''); setNewNote('');
      await loadGlossary();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    finally { setGlossarySaving(false); }
  };

  const removeTerm = async (term) => {
    // الحذف صار فوريّاً على الخادم بلا «حفظ القاموس» يسبقه
    if (!window.confirm(`حذف المصطلح «${term.term}» من القاموس؟`)) return;
    try {
      await api(`/settings/glossary/${term.id}`, { method: 'DELETE' });
      await loadGlossary();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const approveTerm = async (term) => {
    try {
      await api(`/settings/glossary/${term.id}`, { method: 'PUT', body: JSON.stringify({ status: 'active' }) });
      await loadGlossary();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const learnTerms = async () => {
    setGlossarySaving(true);
    try {
      const res = await api('/settings/glossary/learn', { method: 'POST' });
      setMessage({ type: res.created ? 'success' : 'info', text: res.message });
      await loadGlossary();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    finally { setGlossarySaving(false); }
  };

  const saveYtApi = async () => {
    if (!ytApiInput.trim()) return;
    setYtApiSaving(true);
    try {
      const res = await api('/settings/youtube-api', {
        method: 'PUT',
        body: JSON.stringify({ url: ytApiInput.trim() }),
      });
      setYtApi({ url: res.url, source: 'database' });
      setMessage({ type: 'success', text: 'تم حفظ رابط API يوتيوب' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setYtApiSaving(false);
    }
  };

  const saveProxy = async () => {
    setProxySaving(true);
    try {
      setProxy(await api('/settings/http-proxy', {
        method: 'PUT',
        body: JSON.stringify({ url: proxyInput.trim() }),
      }));
      setProxyInput('');
      setMessage({ type: 'success', text: proxyInput.trim() ? 'تم حفظ الوكيل' : 'تم حذف الوكيل' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setProxySaving(false);
    }
  };

  const runProbe = async () => {
    if (!probeUrl.trim()) return;
    setProbing(true);
    setProbeResult(null);
    try {
      setProbeResult(await api('/settings/youtube-probe', {
        method: 'POST',
        body: JSON.stringify({ url: probeUrl.trim() }),
      }));
    } catch (err) {
      setProbeResult({ ok: false, reason: err.message });
    } finally {
      setProbing(false);
    }
  };

  const runPullTest = async () => {
    if (!probeUrl.trim()) return;
    setPulling(true);
    setPullResult(null);
    try {
      setPullResult(await api('/settings/youtube-pull-test', {
        method: 'POST',
        body: JSON.stringify({ url: probeUrl.trim() }),
      }));
    } catch (err) {
      setPullResult({ ok: false, reason: err.message, paths: [] });
    } finally {
      setPulling(false);
    }
  };

  const uploadCookies = async (file) => {
    if (!file) return;
    setCookiesSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      setYtCookies(await apiUpload('/settings/youtube-cookies', fd));
      setMessage({ type: 'success', text: 'حُفظ ملفّ كوكيز يوتيوب' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCookiesSaving(false);
    }
  };

  const clearCookies = async () => {
    setCookiesSaving(true);
    try {
      setYtCookies(await api('/settings/youtube-cookies', { method: 'DELETE' }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCookiesSaving(false);
    }
  };

  const savePublishTarget = async () => {
    setPublishSaving(true);
    try {
      const res = await api('/settings/publish-target', {
        method: 'PUT',
        body: JSON.stringify({ ...publishForm, endpoint: publishForm.endpoint.trim(), token: publishForm.token.trim() }),
      });
      setPublishTarget(res);
      setPublishForm(f => ({ ...f, token: '' }));
      setMessage({ type: 'success', text: 'تم حفظ وجهة النشر' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setPublishSaving(false);
    }
  };

  const saveCobaltKey = async () => {
    setCobaltKeySaving(true);
    try {
      const res = await api('/settings/cobalt-key', {
        method: 'PUT',
        body: JSON.stringify({ api_key: cobaltKeyInput.trim() }),
      });
      setCobaltKey(res);
      setCobaltKeyInput('');
      setMessage({ type: 'success', text: cobaltKeyInput.trim() ? 'تم حفظ Cobalt Api-Key' : 'تم حذف Cobalt Api-Key' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setCobaltKeySaving(false);
    }
  };

  const fetchLog = async () => {
    setLogLoading(true);
    try {
      const qs = logFilter ? `?contains=${encodeURIComponent(logFilter)}` : '';
      const res = await api(`/settings/log${qs}`);
      setLogEntries(res.entries || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'تعذر قراءة السجل' });
    } finally {
      setLogLoading(false);
    }
  };

  const saveDesk = async () => {
    setDeskSaving(true);
    try {
      const saved = await api('/settings/support', { method: 'PUT', body: JSON.stringify(desk) });
      setDesk(saved);
      setMessage({ type: 'success', text: 'تم حفظ إعدادات مكتب الدعم' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'فشل الحفظ' });
    } finally {
      setDeskSaving(false);
    }
  };

  const patchCanned = (index, patch) => setDesk(d => ({
    ...d, canned: d.canned.map((c, i) => (i === index ? { ...c, ...patch } : c)),
  }));

  if (loading) return <div className="loader">جاري التحميل...</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>الإعدادات</h2>
      <div style={{ fontSize: 13, color: 'var(--text-faint)', marginBottom: 24 }}>
        إدارة الخدمات الخارجية المرتبطة بالمنصة
      </div>

      {desk && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>🎫</span>
            <h3 style={{ margin: 0, fontSize: 17 }}>مكتب الدعم — تذاكر العملاء</h3>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>
            ساعات العمل تُحسب عليها مهلة أول رد، والتذكرة التي تتجاوزها تُصعَّد للإدارة تلقائياً.
            الردود الجاهزة تظهر في صفحة طلبات العملاء وتقبل المتغيّرات:{' '}
            <code>{'{العميل}'}</code> <code>{'{رقم}'}</code> <code>{'{العنوان}'}</code> <code>{'{الموظف}'}</code>.
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">بداية الدوام (ساعة)</label>
              <input className="form-input" type="number" min="0" max="23" value={desk.work_start}
                onChange={e => setDesk(d => ({ ...d, work_start: Number(e.target.value) }))} />
            </div>
            <div className="form-group">
              <label className="form-label">نهاية الدوام (ساعة)</label>
              <input className="form-input" type="number" min="1" max="24" value={desk.work_end}
                onChange={e => setDesk(d => ({ ...d, work_end: Number(e.target.value) }))} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">مهلة أول رد (ساعات عمل)</label>
              <input className="form-input" type="number" min="1" max="72" value={desk.first_response_hours}
                onChange={e => setDesk(d => ({ ...d, first_response_hours: Number(e.target.value) }))} />
            </div>
            <div className="form-group">
              <label className="form-label">نافذة إعادة الفتح (أيام)</label>
              <input className="form-input" type="number" min="0" max="90" value={desk.reopen_days}
                onChange={e => setDesk(d => ({ ...d, reopen_days: Number(e.target.value) }))} />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={!!desk.auto_reply}
                onChange={e => setDesk(d => ({ ...d, auto_reply: e.target.checked }))} />
              ردّ آلي على التذاكر الواردة خارج الدوام
            </label>
            {desk.auto_reply && (
              <textarea className="form-textarea" style={{ marginTop: 8, minHeight: 70 }}
                value={desk.auto_reply_body || ''}
                onChange={e => setDesk(d => ({ ...d, auto_reply_body: e.target.value }))} />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">الردود الجاهزة</label>
            {desk.canned.length === 0 ? (
              <div style={{ padding: '10px 0', fontSize: 13, color: '#9ca3af' }}>لا توجد ردود جاهزة بعد</div>
            ) : desk.canned.map((c, i) => (
              <div key={i} style={{ display: 'grid', gap: 6, padding: 10, marginBottom: 8, background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" style={{ flex: 1 }} placeholder="عنوان الرد"
                    value={c.title} onChange={e => patchCanned(i, { title: e.target.value })} />
                  <button className="btn-icon" title="حذف"
                    onClick={() => setDesk(d => ({ ...d, canned: d.canned.filter((_, j) => j !== i) }))}>✕</button>
                </div>
                <textarea className="form-textarea" style={{ minHeight: 70 }} placeholder="نص الرد"
                  value={c.body} onChange={e => patchCanned(i, { body: e.target.value })} />
              </div>
            ))}
            <button className="btn btn-outline btn-sm"
              onClick={() => setDesk(d => ({ ...d, canned: [...d.canned, { title: '', body: '' }] }))}>
              + ردّ جاهز
            </button>
          </div>

          <button className="btn btn-primary" onClick={saveDesk} disabled={deskSaving}>
            {deskSaving ? 'جارٍ الحفظ...' : 'حفظ إعدادات الدعم'}
          </button>
        </div>
      )}

      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🎙️</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>خدمة التفريغ الصوتي — Groq / Fireworks</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          يُستخدم نموذج Whisper Large-v3 لتحويل الملفات الصوتية العربية إلى نصوص دقيقة.
          يُحدَّد المزوّد تلقائياً حسب المفتاح: <code>gsk_</code> ← Groq، <code>fw_</code> ← Fireworks.
        </div>

        <div style={{
          fontSize: 12, color: 'var(--c-gold-ink)', background: 'var(--c-gold-soft)',
          border: '1px solid var(--c-gold)', borderRadius: 8, padding: '10px 12px', marginBottom: 20,
        }}>
          💡 توليد <strong>الملخّص والفصول والتنسيق</strong> يعتمد على نموذج لغة. حسابات
          <strong> Fireworks</strong> كثيراً ما تُفعِّل التفريغ الصوتي دون نماذج اللغة فتفشل هذه الخطوات.
          أضِف مفتاح <strong>Groq مجّاني (<code>gsk_</code>)</strong> في «مفاتيح إضافية» بالأسفل —
          سيُستخدم تلقائياً لخطوات اللغة بينما يبقى التفريغ على Fireworks.
        </div>

        {/* Status banner */}
        <div style={{
          padding: '12px 14px',
          borderRadius: 8,
          background: status?.configured ? 'var(--c-success-soft)' : 'var(--c-gold-soft)',
          border: `1px solid ${status?.configured ? 'var(--c-success)' : 'var(--c-gold-soft)'}`,
          marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{status?.configured ? '✅' : '⚠️'}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: status?.configured ? 'var(--c-success)' : 'var(--c-gold-ink)' }}>
                {status?.configured ? 'مضبوط ومفعّل' : 'لم يتم الضبط بعد'}
              </div>
              {status?.masked_key && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
                  {status.masked_key}
                </div>
              )}
            </div>
          </div>
          {status?.configured && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-sm" onClick={test} disabled={testing}>
                {testing ? 'جارِ الاختبار...' : '🔌 اختبار الاتصال'}
              </button>
              <button type="button" className="btn btn-sm" onClick={remove}
                style={{ color: 'var(--c-danger)', borderColor: 'var(--c-coral)' }}>
                حذف
              </button>
            </div>
          )}
        </div>

        {/* شريحة الاشتراك — تحدّد حدّ حجم الطلب الواحد وسقف الساعة */}
        <div style={{
          padding: '12px 14px', borderRadius: 8, marginBottom: 20,
          border: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10
        }}>
          <input
            type="checkbox"
            id="paid-tier"
            checked={!!status?.paid_tier}
            disabled={paidSaving}
            onChange={(e) => savePaidTier(e.target.checked)}
            style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor="paid-tier" style={{ cursor: 'pointer', flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>اشتراك Groq مدفوع (Dev Tier)</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.8 }}>
              الاشتراك المجاني يقبل 25 ميغابايت للطلب الواحد وساعتَي صوت في الساعة، والمدفوع يرفعهما إلى
              100 ميغابايت وبلا سقف ساعي. المفتاح لا يكشف الشريحة، فحدّدها هنا.
              {status?.max_mb ? ` الحدّ المُطبَّق الآن: ${status.max_mb} ميغابايت للطلب.` : ''}
            </div>
          </label>
        </div>

        {/* حصّة الساعة — طابور التفريغ يقسّم الدروس عليها ويحدّد مواعيدها */}
        <div style={{ padding: '12px 14px', borderRadius: 8, marginBottom: 20, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>حصّة الصوت في الساعة لكل مفتاح (بالثواني)</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 8px', lineHeight: 1.8 }}>
            يحدّدها حساب Groq لا المنصّة. طابور التفريغ لا يرسل جزءاً لا تتّسع له، ويعرض موعد كل درس على أساسها.
            {status?.hourly_audio_seconds ? ` المُطبَّق الآن: ${status.hourly_audio_seconds} ثانية.` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              min="600"
              className="form-input"
              placeholder={status?.hourly_audio_seconds ? String(status.hourly_audio_seconds) : '7200'}
              value={quotaDraft}
              onChange={(e) => setQuotaDraft(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <button className="btn btn-sm btn-outline" disabled={quotaSaving || !quotaDraft} onClick={saveHourlyQuota}>حفظ</button>
          </div>
        </div>

        <ConnectorAutoApprove />

        {/* استخراج الفوائد تلقائياً — قرارٌ في الكلفة لا إعداد عابر */}
        <div style={{
          padding: '12px 14px', borderRadius: 8, marginBottom: 20,
          border: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10
        }}>
          <input
            type="checkbox"
            id="auto-benefits"
            checked={!!status?.auto_benefits}
            disabled={autoBenefitsSaving}
            onChange={(e) => saveAutoBenefits(e.target.checked)}
            style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor="auto-benefits" style={{ cursor: 'pointer', flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>استخراج الفوائد تلقائياً بعد كل تفريغ</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.8 }}>
              استخراج فوائد درس ساعتين نحو خمسة وعشرين نداءً للنموذج — أضعاف كلفة الملخّص والفصول معاً.
              مطفأً، يبقى زرّ «استخراج» في صفحة التفريغ يعمل عند الطلب.
            </div>
          </label>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: message.type === 'error' ? 'var(--c-coral-soft)'
            : message.type === 'info' ? 'var(--c-blue-100)' : 'var(--c-success-soft)',
            color: message.type === 'success' ? 'var(--c-success)' : 'var(--c-danger)',
            border: `1px solid ${message.type === 'success' ? 'var(--c-success)' : 'var(--c-coral)'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Form */}
        <form onSubmit={save}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
            {status?.configured ? 'تحديث المفتاح' : 'مفتاح API'}
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="مفتاح Groq (gsk_) أو Fireworks (fw_)"
              className="form-input"
              style={{ flex: 1, fontFamily: 'monospace' }}
              autoComplete="off"
            />
            <button type="submit" className="btn btn-primary" disabled={saving || !apiKey.trim()}>
              {saving ? 'جارِ الحفظ...' : 'حفظ'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
            احصل على مفتاحك من{' '}
            <a href="https://console.groq.com/keys" target="_blank" rel="noreferrer"
              style={{ color: 'var(--c-blue-500)' }}>
              console.groq.com/keys
            </a>
            {' '}أو{' '}
            <a href="https://fireworks.ai/account/api-keys" target="_blank" rel="noreferrer"
              style={{ color: 'var(--c-blue-500)' }}>
              fireworks.ai
            </a>
            {status?.source === 'env' && (
              <span style={{ marginRight: 10, color: 'var(--c-success)' }}>
                • مفتاح من .env مُستخدم حاليًا
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Multi-key Groq card — extra keys give independent hourly quotas */}
      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>⚡</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>مفاتيح إضافية — تفريغ متوازٍ</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
          أضف مفاتيح من <strong>حسابات Groq أو Fireworks منفصلة</strong> لتفريغ الملفات الطويلة بشكل أسرع.
          كل مفتاح له حصّة ساعية مستقلّة (٧٢٠٠ ثانية)، فمفتاحان = أربع ساعات صوت/ساعة، ثلاثة = ست ساعات/ساعة، إلخ.
          عند تجزئة ملف طويل، النظام يوزّع الأجزاء على المفاتيح بالتناوب.
          <br/>
          <span style={{ color: 'var(--c-danger)' }}>⚠️ مفاتيح من نفس الحساب لن تساعد لأنها تشترك في نفس الحصّة.</span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="password"
            className="form-input"
            value={newGroqKey}
            onChange={e => setNewGroqKey(e.target.value)}
            placeholder="أضف مفتاحاً إضافياً (gsk_ أو fw_)"
            style={{ flex: 1, fontFamily: 'monospace' }}
            autoComplete="off"
          />
          <button
            className="btn btn-primary"
            onClick={addGroqKey}
            disabled={groqKeysSaving || !newGroqKey.trim()}
          >
            {groqKeysSaving ? '...' : '+ إضافة'}
          </button>
        </div>

        {groqKeys.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-faint)', padding: 14, textAlign: 'center' }}>
            لا توجد مفاتيح بعد. ابدأ بإضافة المفتاح الأوّل من الحقل في الأعلى.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groqKeys.map((maskedKey, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 10, padding: '8px 12px',
                background: 'var(--surface-alt)', borderRadius: 8, border: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="badge badge-completed" style={{ fontSize: 10 }}>#{i + 1}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{maskedKey}</span>
                </div>
                <button
                  onClick={() => removeGroqKey(i)}
                  disabled={groqKeysSaving}
                  style={{ background: 'none', border: 'none', color: 'var(--c-coral)', cursor: 'pointer', fontSize: 14 }}
                  title="حذف"
                >🗑️</button>
              </div>
            ))}
          </div>
        )}

        {groqKeys.length > 1 && (
          <div style={{ marginTop: 12, padding: 10, background: 'var(--c-success-soft)', borderRadius: 8, fontSize: 12, color: 'var(--c-success)' }}>
            ✅ تفريغ متوازٍ مفعّل: ملف من {groqKeys.length} أجزاء يكتمل بدون انتظار.
          </div>
        )}
      </div>

      {/* وجهة النشر — البند ٢٢ */}
      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🌐</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>النشر إلى موقع الشيخ</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
          يُرسل الدرس المجهَّز إلى الموقع مباشرة بدل تصدير Word ولصقه يدوياً. الافتراض أن يصل
          «مسوّدة» على الموقع لا «منشوراً»، ولا يُرسل شيء إلا بضغطة من يملك صلاحية
          «النشر إلى موقع الشيخ»، ويُسجَّل باسمه.
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>نوع الوجهة</label>
        <select
          className="form-input"
          value={publishForm.mode}
          onChange={e => setPublishForm(f => ({ ...f, mode: e.target.value }))}
          style={{ width: '100%' }}
        >
          <option value="">معطّل</option>
          <option value="wordpress">ووردبريس (REST)</option>
          <option value="webhook">خطّاف عامّ (JSON)</option>
        </select>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
          {publishForm.mode === 'wordpress' ? 'عنوان الموقع (بلا /wp-json)' : 'عنوان الخطّاف'}
        </label>
        <input
          className="form-input"
          value={publishForm.endpoint}
          onChange={e => setPublishForm(f => ({ ...f, endpoint: e.target.value }))}
          placeholder={publishForm.mode === 'wordpress' ? 'https://alkathiri.net' : 'https://alkathiri.net/api/lessons'}
          style={{ width: '100%', fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
          رمز الدخول — «مستخدم:كلمة‌تطبيق» لووردبريس، أو رمز حامل
        </label>
        <input
          type="password"
          className="form-input"
          value={publishForm.token}
          onChange={e => setPublishForm(f => ({ ...f, token: e.target.value }))}
          placeholder={publishTarget?.has_token ? publishTarget.masked_token : 'لم يُضبط'}
          style={{ width: '100%', fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
          اتركه فارغاً كي يبقى الرمز المحفوظ كما هو.
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
          حالة المقال على الموقع
        </label>
        <select
          className="form-input"
          value={publishForm.default_status}
          onChange={e => setPublishForm(f => ({ ...f, default_status: e.target.value }))}
          style={{ width: '100%' }}
        >
          <option value="draft">مسوّدة — تُراجَع على الموقع قبل الظهور</option>
          <option value="publish">منشور مباشرة</option>
        </select>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={savePublishTarget} disabled={publishSaving}>
            {publishSaving ? 'جارِ الحفظ...' : 'حفظ'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>
            {publishTarget?.configured ? '✅ الوجهة مضبوطة' : '⚠️ غير مضبوطة — زرّ النشر سيرفض'}
          </span>
        </div>
      </div>

      {/* Cobalt API endpoint card */}
      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🎬</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>تحميل من المنصّات — Cobalt API</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
          يستخدم Cobalt لاستخراج صوت الفيديو من يوتيوب وتيك‌توك وإنستجرام وX وفيسبوك...
          (لا يحتاج yt-dlp / ffmpeg على السيرفر). الـ instance الرسمي
          {' '}<code>api.cobalt.tools</code> محجوب من الخوادم؛ استخدم instance عاماً يقبل Api-Key
          من <a href="https://instances.cobalt.best" target="_blank" rel="noreferrer" style={{ color: 'var(--c-blue-500)' }}>instances.cobalt.best</a>{' '}
          أو شغّل instance خاصاً.
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          رابط API
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            value={ytApiInput}
            onChange={e => setYtApiInput(e.target.value)}
            placeholder="https://api.cobalt.tools/"
            style={{ flex: 1, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
          />
          <button className="btn btn-primary" onClick={saveYtApi} disabled={ytApiSaving || !ytApiInput.trim()}>
            {ytApiSaving ? 'جارِ الحفظ...' : 'حفظ'}
          </button>
        </div>
        {ytApi?.source && (
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
            مصدر القيمة الحالية: {
              ytApi.source === 'database' ? 'قاعدة بيانات (محفوظ من هنا)' :
              ytApi.source === 'env' ? 'ملف .env' : 'القيمة الافتراضية'
            }
          </div>
        )}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
          Api-Key (اختياري — يحتاجه معظم instances العامة)
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="password"
            className="form-input"
            value={cobaltKeyInput}
            onChange={e => setCobaltKeyInput(e.target.value)}
            placeholder={cobaltKey?.configured ? cobaltKey.masked_key : '00000000-1111-2222-3333-444444444444'}
            style={{ flex: 1, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
          />
          <button className="btn btn-primary" onClick={saveCobaltKey} disabled={cobaltKeySaving}>
            {cobaltKeySaving ? 'جارِ الحفظ...' : (cobaltKeyInput.trim() ? 'حفظ' : 'مسح')}
          </button>
        </div>
        {cobaltKey && (
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
            {cobaltKey.configured
              ? `مضبوط (${cobaltKey.source === 'database' ? 'قاعدة بيانات' : 'ملف .env'}): ${cobaltKey.masked_key}`
              : 'لم يُضبط — قد تفشل الطلبات على instances العامة'}
          </div>
        )}
      </div>

      {/* Outbound proxy + YouTube diagnostics card */}
      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🛰️</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>وكيل الطلبات الصادرة وتشخيص يوتيوب</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
          يوتيوب يرفض طلبات عناوين مراكز البيانات — وعنوان الاستضافة المشتركة منها — فيعيد 403
          قبل أن يصل الطلب إلى بيانات الفيديو. ضبط وكيل هنا يجعل الطلبات تخرج من عنوان آخر، وهو
          الحلّ الوحيد الذي لا يعتمد على خدمة وسيطة. يُستخدم أيضاً لطلبات Cobalt والتنزيل المباشر.
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          رابط الوكيل (اختياري)
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            value={proxyInput}
            onChange={e => setProxyInput(e.target.value)}
            placeholder={proxy?.configured ? proxy.url : 'http://user:pass@host:8080'}
            style={{ flex: 1, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
          />
          <button className="btn btn-primary" onClick={saveProxy} disabled={proxySaving}>
            {proxySaving ? 'جارِ الحفظ...' : (proxyInput.trim() ? 'حفظ' : 'مسح')}
          </button>
        </div>
        {proxy && (
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
            {proxy.configured
              ? `مضبوط: ${proxy.url}${proxy.active ? '' : ' — الصيغة غير مقبولة فلن يُستخدم'}`
              : 'لم يُضبط — الطلبات تخرج من عنوان الخادم مباشرة'}
          </div>
        )}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 20 }}>
          فحص رابط يوتيوب
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            value={probeUrl}
            onChange={e => setProbeUrl(e.target.value)}
            placeholder="https://youtu.be/..."
            style={{ flex: 1, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}
          />
          <button className="btn btn-outline" onClick={runProbe} disabled={probing || !probeUrl.trim()}>
            {probing ? 'جارِ الفحص...' : 'فحص'}
          </button>
          <button className="btn btn-primary" onClick={runPullTest} disabled={pulling || !probeUrl.trim()}>
            {pulling ? 'جارِ السحب...' : 'اختبار سحب يوتيوب'}
          </button>
        </div>
        {pullResult && (
          <div data-testid="pull-result" style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 12, lineHeight: 1.8, background: 'var(--surface-alt)', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {pullResult.ok ? '✅ ' : '❌ '}{pullResult.reason}
            </div>
            {pullResult.title && <div>العنوان: {pullResult.title}</div>}
            {pullResult.config && (
              <div>الوكيل: {pullResult.config.proxy} · الكوكيز: {pullResult.config.cookies} · Cobalt: {pullResult.config.cobalt}</div>
            )}
            {(pullResult.paths || []).map(p => (
              <div key={p.path}>{p.ok ? '✅' : '❌'} {p.path}: {p.detail}</div>
            ))}
          </div>
        )}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, marginTop: 20 }}>
          ملفّ cookies لحساب يوتيوب (صيغة Netscape)
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".txt,text/plain" disabled={cookiesSaving}
            onChange={e => { uploadCookies(e.target.files?.[0]); e.target.value = ''; }} />
          {ytCookies?.source === 'database' && (
            <button className="btn btn-outline btn-sm" onClick={clearCookies} disabled={cookiesSaving}>حذف الملفّ</button>
          )}
        </div>
        {ytCookies && (
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>
            {ytCookies.configured
              ? `مضبوط (${ytCookies.source === 'env' ? 'من .env' : 'مرفوع'}) — ${ytCookies.count} كوكي صالحة${ytCookies.signed_in ? '، بحساب مسجّل' : '، بلا SAPISID فلن يُوقَّع الطلب'}`
              : 'لم يُضبط — يُرفع هنا أو يُشار إليه بـ YTDLP_COOKIES_FILE'}
          </div>
        )}
        {probeResult && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 12, lineHeight: 1.8, background: 'var(--surface-alt)', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {probeResult.ok ? '✅ يوتيوب استجاب' : '❌ يوتيوب لم يستجب'}
              {probeResult.client ? ` — عبر ${probeResult.client}` : ''}
            </div>
            {probeResult.reason && <div style={{ marginBottom: 6 }}>{probeResult.reason}</div>}
            {probeResult.title && <div>العنوان: {probeResult.title}</div>}
            {probeResult.seconds > 0 && <div>المدّة: {Math.round(probeResult.seconds / 60)} دقيقة</div>}
            {probeResult.proxy && <div>الوكيل: {probeResult.proxy}</div>}
            {probeResult.audio && <div>الصوت: {probeResult.audio.ext} — {Math.round(probeResult.audio.bitrate / 1000)}kbps</div>}
            {Array.isArray(probeResult.captions) && (
              <div>
                الترجمات: {probeResult.captions.length
                  ? probeResult.captions.map(c => `${c.lang}${c.asr ? ' (آلية)' : ' (يدوية)'}`).join(' · ')
                  : 'لا يوجد'}
              </div>
            )}
            {Array.isArray(probeResult.attempts) && (
              <div style={{ marginTop: 6, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left', fontSize: 11 }}>
                {probeResult.attempts.map(a => <div key={a.client}>{a.client}: {a.result}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Glossary card */}
      <div className="card" style={{ padding: 24, marginTop: 16 }} data-tour="settings-glossary">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>📖</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>قاموس مصطلحات التفريغ</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          أسماء المشايخ والكتب والمصطلحات ليلتزمها التنسيق. لكلّ شيخ قاموسه — أسماء شيوخه وكتبه تخصّه،
          وخلطها بغيره يُدخل على درسه ما ليس من كلامه. والقاموس العامّ يُحقن مع كل درس.
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <select
            className="form-select"
            value={glossaryScope}
            onChange={e => { setGlossaryScope(e.target.value); loadGlossary(e.target.value); }}
            style={{ maxWidth: 260 }}
          >
            <option value="">الكل</option>
            <option value="global">القاموس العامّ</option>
            {sheikhs.map(sh => <option key={sh.id} value={sh.id}>{sh.name}</option>)}
          </select>

          <button className="btn btn-outline btn-sm" onClick={learnTerms} disabled={glossarySaving}>
            🧠 اقترح من التصحيحات
          </button>

          {glossarySuggested > 0 && (
            <span className="badge badge-waiting_approval" style={{ fontSize: 11 }}>
              {glossarySuggested} اقتراحاً ينتظر اعتمادك
            </span>
          )}
        </div>

        {/* Existing items */}
        {glossary.length === 0 ? (
          <div style={{ padding: '14px 0', fontSize: 13, color: 'var(--text-faint)', textAlign: 'center' }}>
            لا توجد مصطلحات بعد
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {glossary.map(g => (
              <div key={g.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
                background: g.status === 'suggested' ? 'var(--c-gold-soft)' : 'var(--surface-alt)',
              }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{g.term}</span>
                  <span style={{ marginRight: 8, fontSize: 11, color: 'var(--text-faint)' }}>
                    {g.client ? g.client.name : 'عامّ'}
                  </span>
                  {g.status === 'suggested' && (
                    <span className="badge badge-waiting_approval" style={{ fontSize: 10, marginRight: 6 }}>مقترَح</span>
                  )}
                  {g.note && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{g.note}</div>}
                </div>
                {g.status === 'suggested' && (
                  <button className="btn btn-sm btn-outline" onClick={() => approveTerm(g)}>✓ اعتمد</button>
                )}
                <button
                  onClick={() => removeTerm(g)}
                  style={{ background: 'none', border: 'none', color: 'var(--c-coral)', cursor: 'pointer', fontSize: 14 }}
                  title="حذف"
                >🗑️</button>
              </div>
            ))}
          </div>
        )}

        {/* Add new */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 12 }}>
          <input
            className="form-input"
            placeholder="المصطلح *"
            value={newTerm}
            onChange={e => setNewTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTerm()}
          />
          <input
            className="form-input"
            placeholder="ملاحظة (اختياري)"
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTerm()}
          />
          <button className="btn btn-outline" onClick={addTerm} disabled={!newTerm.trim() || glossarySaving}>+ إضافة</button>
        </div>
      </div>

      {/* Log viewer card */}
      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🩺</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>سجلّ الأخطاء (laravel.log)</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          آخر ١٠٠ مدخل من سجلّ Laravel — مفيد لتشخيص أخطاء التفريغ أو غيرها بسرعة دون SSH.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            className="form-input"
            placeholder="فلترة (مثلاً: TranscribeAudioJob, Whisper, ERROR)"
            value={logFilter}
            onChange={e => setLogFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchLog()}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" onClick={fetchLog} disabled={logLoading}>
            {logLoading ? 'جاري...' : '🔄 تحديث'}
          </button>
        </div>

        {logEntries === null ? (
          <div style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', padding: 14 }}>
            اضغط "تحديث" لعرض آخر مدخلات السجل
          </div>
        ) : logEntries.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', padding: 14 }}>
            لا توجد مدخلات مطابقة
          </div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
            {logEntries.map((entry, i) => {
              const isError = /ERROR|EMERGENCY|CRITICAL/i.test(entry);
              const isWarning = /WARNING/i.test(entry);
              const firstLine = entry.split('\n')[0];
              const rest = entry.split('\n').slice(1).join('\n');
              return (
                <details key={i} style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border)',
                  background: isError ? 'var(--c-coral-soft)' : isWarning ? 'var(--c-gold-soft)' : '#fff',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  lineHeight: 1.5,
                  direction: 'ltr',
                  textAlign: 'left',
                }}>
                  <summary style={{ cursor: 'pointer', fontWeight: isError ? 600 : 400, color: isError ? 'var(--c-danger)' : isWarning ? 'var(--c-gold-ink)' : 'var(--text-body)', wordBreak: 'break-word' }}>
                    {firstLine}
                  </summary>
                  {rest && (
                    <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-muted)', fontSize: 10 }}>
                      {rest}
                    </pre>
                  )}
                </details>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
